import { config } from "@targeted-support/config";
import { getBenchmarkCasesForOrganisation } from "../benchmark/benchmarkCases";
import type { BenchmarkCase } from "../benchmark/types";
import type { Persona } from "./types";

/**
 * References a benchmark case by ID, adding persona-specific display metadata.
 * The situationSummary, personaDetail, and targetSuggestion are pulled from
 * the benchmark case's modifiedCase at resolution time.
 */
interface PersonaBenchmarkRef {
  id: string;
  name: string;
  description: string;
  icon: string;
  benchmarkCaseId: number;
}

type PersonaConfig = PersonaBenchmarkRef | Persona;

function resolvePersonaConfig(
  personaConfig: PersonaConfig,
  benchmarkCases: BenchmarkCase[],
): Persona {
  if (!("benchmarkCaseId" in personaConfig)) return personaConfig;

  const benchmarkCase = benchmarkCases.find(
    (c) => c.id === personaConfig.benchmarkCaseId,
  );
  if (!benchmarkCase) {
    throw new Error(
      `Benchmark case ${personaConfig.benchmarkCaseId} not found for persona "${personaConfig.id}"`,
    );
  }
  const { situationSummary, personaDetail, expectedSuggestion } =
    benchmarkCase.modifiedCase;
  return {
    id: personaConfig.id,
    name: personaConfig.name,
    description: personaConfig.description,
    situationSummary,
    personaDetail,
    targetSuggestion: expectedSuggestion,
    icon: personaConfig.icon,
  };
}

const DEMO_PERSONA_CONFIGS: PersonaConfig[] = [
  {
    id: "young_saver",
    name: "Young Saver",
    description: "Early career professional just starting to save and invest",
    icon: "PiggyBank",
    benchmarkCaseId: 1,
  },
  {
    id: "experienced_investor",
    name: "Experienced Investor",
    description:
      "Self-directed investor with high-risk holdings looking to consolidate into a tax-efficient wrapper",
    icon: "TrendingUp",
    benchmarkCaseId: 11,
  },
  {
    id: "retirement_planner",
    name: "Retirement Planner",
    description:
      "Approaching retirement and looking to secure their future",
    icon: "Armchair",
    benchmarkCaseId: 4,
  },
  {
    id: "debt_conscious",
    name: "Debt Conscious",
    description: "Balancing debt repayment with starting to save",
    icon: "Scale",
    benchmarkCaseId: 2,
  },
  {
    id: "high_net_worth",
    name: "High Net Worth",
    description:
      "High-income individual with significant assets to invest",
    icon: "Gem",
    benchmarkCaseId: 5,
  },
  {
    id: "first_time_buyer",
    name: "First-Time Buyer",
    description: "Young adult saving to buy their first home",
    icon: "Home",
    benchmarkCaseId: 3,
  },
];

const LLOYDS_PERSONA_CONFIGS: PersonaConfig[] = [
  {
    id: "young_saver",
    name: "Young Saver",
    description: "Early career professional just starting to save and invest",
    icon: "PiggyBank",
    benchmarkCaseId: 1,
  },
  {
    id: "experienced_investor",
    name: "Experienced Investor",
    description: "Seasoned investor looking to optimise their portfolio",
    icon: "TrendingUp",
    benchmarkCaseId: 52,
  },
  {
    id: "retirement_planner",
    name: "Retirement Planner",
    description:
      "Approaching retirement and looking to secure their future",
    icon: "Armchair",
    benchmarkCaseId: 26,
  },
  {
    id: "debt_conscious",
    name: "Debt Conscious",
    description: "Balancing debt repayment with starting to save",
    icon: "Scale",
    benchmarkCaseId: 3,
  },
  {
    id: "high_net_worth",
    name: "High Net Worth",
    description:
      "High-income individual with significant assets to invest",
    icon: "Gem",
    benchmarkCaseId: 30,
  },
  {
    id: "first_time_buyer",
    name: "First-Time Buyer",
    description: "Young adult saving to buy their first home",
    icon: "Home",
    benchmarkCaseId: 6,
  },
];

const NATWEST_PERSONA_CONFIGS: PersonaConfig[] = [
  {
    id: "young_saver",
    name: "Young Saver",
    description: "Early career professional just starting to save and invest",
    icon: "PiggyBank",
    benchmarkCaseId: 1,
  },
  {
    id: "experienced_investor",
    name: "Experienced Investor",
    description: "Seasoned investor looking to optimise their portfolio",
    icon: "TrendingUp",
    benchmarkCaseId: 9,
  },
  {
    id: "retirement_planner",
    name: "Retirement Planner",
    description:
      "Approaching retirement and looking to secure their future",
    icon: "Armchair",
    benchmarkCaseId: 4,
  },
  {
    id: "debt_conscious",
    name: "Debt Conscious",
    description: "Balancing debt repayment with starting to save",
    icon: "Scale",
    benchmarkCaseId: 2,
  },
  {
    id: "high_net_worth",
    name: "High Net Worth",
    description:
      "High-income individual with significant assets to invest",
    icon: "Gem",
    benchmarkCaseId: 5,
  },
  {
    id: "first_time_buyer",
    name: "First-Time Buyer",
    description: "Young adult saving to buy their first home",
    icon: "Home",
    benchmarkCaseId: 3,
  },
];

const CONFIGS_BY_ORG: Record<string, PersonaConfig[]> = {
  [config.DEMO_ORGANISATION_ID]: DEMO_PERSONA_CONFIGS,
  [config.LLOYDS_ORGANISATION_ID]: LLOYDS_PERSONA_CONFIGS,
  [config.NATWEST_ORGANISATION_ID]: NATWEST_PERSONA_CONFIGS,
};

let resolvedPersonasByOrg: Record<string, Persona[]> | null = null;

function buildPersonasByOrg(): Record<string, Persona[]> {
  if (resolvedPersonasByOrg) return resolvedPersonasByOrg;

  resolvedPersonasByOrg = {};
  for (const [orgId, configs] of Object.entries(CONFIGS_BY_ORG)) {
    const benchmarkCases = getBenchmarkCasesForOrganisation(orgId);
    resolvedPersonasByOrg[orgId] = configs.map((c) =>
      resolvePersonaConfig(c, benchmarkCases),
    );
  }
  return resolvedPersonasByOrg;
}

export function getPersonasForOrganisation(
  tenantId: string | undefined,
): Persona[] {
  const map = buildPersonasByOrg();
  if (tenantId && map[tenantId]) {
    return map[tenantId];
  }
  return map[config.DEMO_ORGANISATION_ID];
}
